import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppShell } from '../../components';
import { Header } from '../../components/Header';
import { NotificationDropdown } from '../../components/NotificationDropdown';
import { UserDropdown } from '../../components/UserDropdown';

import { useAuth, useSignOut } from '@/hooks/auth';

/**
 * PrivateHeader - Header component for authenticated pages
 *
 * Renders the shell `Header`, driven entirely by AppShell context:
 * - **Default (`main`)**: hamburger, brand, search, notifications, avatar.
 * - **Page takeover**: a page pushes a `HeaderConfig` via the `useHeaderConfig`
 *   hook (e.g. form pages driving the `action` variant). The config supplies the
 *   variant, title, primary action, and close handler; dropdowns and search are
 *   shown only for the `main` variant.
 *
 * Integrates `UserDropdown` + `NotificationDropdown` (main variant only) and the
 * hamburger menu (mobile drawer / sidebar toggle).
 *
 * @example
 * ```tsx
 * // Rendered once by the layout — no props; behavior comes from context.
 * <PrivateHeader />
 *
 * // A form page takes over the header:
 * useHeaderConfig(
 *   () => ({
 *     variant: 'action',
 *     title: 'New Workout',
 *     primaryAction: { label: 'Save', onClick: handleSave, disabled: !name },
 *     onClose: handleBack,
 *   }),
 *   [name, handleSave, handleBack]
 * );
 * ```
 */
export function PrivateHeader(): React.JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sidebarBehavior, toggleMobileDrawer, toggleSidebar, headerConfig } =
    useAppShell();

  // Handle menu button click - mobile opens drawer, tablet/desktop toggles sidebar
  const handleMenuClick = useCallback(() => {
    if (sidebarBehavior === 'hidden') {
      toggleMobileDrawer();
    } else {
      toggleSidebar();
    }
  }, [sidebarBehavior, toggleMobileDrawer, toggleSidebar]);

  // Sign out from the user dropdown — or leave, for an admin signed in as
  // the member.
  const { signOut: handleSignOut, label: signOutLabel } = useSignOut();

  // Every menu row but Sign Out and Adjust Goals is a destination, and the
  // manifest already carries its href — so one handler covers all of them.
  const handleMenuNavigate = useCallback(
    (href: string) => navigate(href),
    [navigate]
  );

  // User dropdown component with auth context data
  const userDropdownElement = user ? (
    <UserDropdown
      user={{
        username: user.username,
        avatar: user.avatar,
        app_invitations_sent: user.app_invitations_sent,
      }}
      role={user.role}
      isOnline
      onNavigate={handleMenuNavigate}
      onSignOut={handleSignOut}
      signOutLabel={signOutLabel}
    />
  ) : null;

  // Notification dropdown component
  const notificationDropdownElement = <NotificationDropdown />;

  // Pages drive the header via `useHeaderConfig`; default to the main variant.
  const variant = headerConfig?.variant ?? 'main';
  const isMain = variant === 'main';

  return (
    <Header
      variant={variant}
      title={headerConfig?.title}
      onMenuClick={handleMenuClick}
      onCloseClick={headerConfig?.onClose}
      onBackClick={headerConfig?.onBack}
      contextMenuItems={headerConfig?.contextMenuItems}
      primaryAction={headerConfig?.primaryAction}
      showSearch={isMain}
      notificationDropdown={isMain ? notificationDropdownElement : undefined}
      userDropdown={isMain ? userDropdownElement : undefined}
      hideBrandOnDesktop
    />
  );
}

export default PrivateHeader;
